import React, { useState, useEffect } from 'react';
import { videoAPI } from '../services/api';
import { Loader2, Youtube, ExternalLink, Play } from 'lucide-react';

const VideoCard = ({ video }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    // YouTube Thumbnail URL (High Res)
    const thumbnailUrl = `https://img.youtube.com/vi/${video.embedId}/maxresdefault.jpg`;

    return (
        <div
            className="group relative bg-[#001524]/80 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-xl hover:shadow-red-500/20 hover:border-red-500/30 hover:-translate-y-2 transition-all duration-500 h-full flex flex-col"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Thumbnail / Video Area */}
            <div
                className="aspect-video w-full relative overflow-hidden bg-black cursor-pointer group/video"
                onClick={() => setIsPlaying(true)}
            >
                {!isPlaying ? (
                    <>
                        {/* Thumbnail Image */}
                        <img
                            src={thumbnailUrl}
                            alt={video.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover/video:scale-110 opacity-90 group-hover/video:opacity-100"
                        />

                        {/* Overlay Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover/video:opacity-60 transition-opacity duration-300" />

                        {/* Pulsing Play Button */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="relative">
                                <div className="absolute inset-0 bg-red-600 rounded-full blur-xl opacity-40 animate-pulse group-hover/video:opacity-60 transition-opacity duration-300"></div>
                                <div className="relative bg-red-600/90 text-white rounded-full p-4 md:p-5 shadow-2xl transform transition-transform duration-300 group-hover/video:scale-110 group-hover/video:rotate-3 border border-red-400/50 backdrop-blur-sm">
                                    <Play className="h-6 w-6 md:h-8 md:w-8 fill-current ml-1" />
                                </div>
                            </div>
                        </div>

                        {/* Duration Badge (Mock - YouTube API doesn't give this easily without extra calls, or we can leave it out) 
                            Let's skip duration to avoid complexity, but add a "Watch Now" badge
                        */}
                        <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded-md border border-white/10 opacity-0 group-hover/video:opacity-100 transition-all duration-300 transform translate-y-2 group-hover/video:translate-y-0">
                            WATCH NOW
                        </div>
                    </>
                ) : (
                    <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${video.embedId}?autoplay=1`}
                        title={video.title || "YouTube video player"}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-full relative z-10"
                    ></iframe>
                )}
            </div>

            {/* Content Area */}
            <div className="p-6 flex flex-col flex-1">
                {/* Title */}
                <h3 className="font-extrabold text-lg md:text-xl text-white line-clamp-2 mb-3 leading-tight group-hover:text-red-400 transition-colors">
                    {video.title}
                </h3>

                {/* Description */}
                <p className="text-gray-400 text-sm line-clamp-3 mb-4 leading-relaxed font-medium">
                    {video.description}
                </p>

                {/* Footer */}
                <div className="mt-auto pt-4 border-t border-white/10 flex items-center justify-between">
                    <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest flex items-center gap-1.5">
                        <Youtube className="h-3 w-3 text-red-500" />
                        {new Date(video.createdAt).toLocaleDateString()}
                    </span>

                    <a
                        href={video.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-red-400 hover:text-white transition-colors group/link"
                        onClick={(e) => e.stopPropagation()} // Prevent playing when clicking link
                    >
                        Open in YouTube
                        <ExternalLink className="h-3 w-3 group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5 transition-transform" />
                    </a>
                </div>
            </div>
        </div>
    );
};

const VideoGallery = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchVideos = async () => {
            try {
                const response = await videoAPI.getAllVideos();
                setVideos(response.data);
            } catch (error) {
                console.error('Failed to load videos:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchVideos();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center py-32">
                <Loader2 className="h-10 w-10 animate-spin text-red-500" />
            </div>
        );
    }

    return (
        <section id="videos" className="py-24 md:py-32 relative z-10">
            {/* Background Ambience */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-red-600/5 rounded-full blur-[100px] -z-10 pointer-events-none"></div>

            <div className="container mx-auto px-4 md:px-6">
                {/* Section Header (Always Visible) */}
                <div className="flex flex-col items-center text-center mb-16 md:mb-24">
                    <div className="inline-flex items-center gap-3 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-black mb-8 backdrop-blur-md shadow-lg">
                        <div className="flex items-center justify-center p-1 bg-red-600 rounded-md shadow-[0_0_12px_rgba(220,38,38,0.4)]">
                            <Youtube className="h-3.5 w-3.5 text-white" />
                        </div>
                        <span className="tracking-[0.2em] uppercase opacity-90">Visual Insights</span>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black text-white mb-6 tracking-tight">
                        Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Video Gallery</span>
                    </h2>
                    <p className="text-lg md:text-xl text-gray-300 max-w-2xl font-medium leading-relaxed">
                        Discover our latest updates, tech guides, and system demonstrations.
                    </p>
                </div>

                {!loading && videos.length === 0 ? (
                    /* PREMIUM EMPTY STATE */
                    <div className="relative overflow-hidden rounded-[2.5rem] bg-[#001524]/60 backdrop-blur-xl border border-white/5 p-12 md:p-24 text-center group">
                        {/* Interactive Background Glow */}
                        <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>

                        <div className="relative z-10 flex flex-col items-center max-w-2xl mx-auto">
                            <div className="relative mb-8">
                                <div className="absolute inset-0 bg-red-500 blur-2xl opacity-20 animate-pulse"></div>
                                <div className="relative bg-white/5 border border-white/10 p-6 rounded-3xl backdrop-blur-md shadow-2xl">
                                    <Play className="h-16 w-16 text-white/50 fill-white/10" />
                                </div>
                            </div>

                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Something Epic is Brewing
                            </h3>
                            <p className="text-lg text-gray-400 leading-relaxed mb-8">
                                We are currently curating a collection of high-definition tech insights, tutorials, and success stories. Stay tuned for a visual journey into the future of IT.
                            </p>

                            <div className="flex items-center gap-2 px-6 py-3 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-bold uppercase tracking-widest animate-pulse">
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                Coming Soon
                            </div>
                        </div>
                    </div>
                ) : (
                    /* PREMIUM GRID Layout */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-2 md:px-0">
                        {videos.map((video, i) => (
                            <div
                                key={video.id || i}
                                className="h-full"
                            >
                                <VideoCard video={video} />
                            </div>
                        ))}
                    </div>
                )}

                {/* View All Button */}
                {!loading && videos.length > 0 && (
                    <div className="mt-16 text-center">
                        <a
                            href="https://www.youtube.com/@Jay_Info_Tech"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold transition-all hover:scale-105 active:scale-95 group"
                        >
                            Visit Our Channel
                            <ExternalLink className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                    </div>
                )}
            </div>
        </section>
    );
};

export default VideoGallery;
